import { Redirect } from 'react-router'
import { Router } from 'react-router-dom'
import { IntlProvider } from 'react-intl'
import Helmet from 'react-helmet'

import 'i18n'

import { pathLanguageSelector } from 'i18n/selectors'
import { stripLanguage, createPath } from 'i18n/utils'
import {
    wordpressLanguageSelector,
    configurationPendingSelector,
    configurationErrorSelector,
    languagesSelector,
} from 'wordpress/selectors'
import Favicon from 'favicon'

import Routes from './components/Routes'
import PleaseWait from './components/PleaseWait'
import '../bootstrap/fonts.scss'
import '../bootstrap/bootstrap.scss'
import { pathSelector } from 'browser-location'
import { isAuthVisibleSelector } from 'header/selectors'
import NotFound from '../not-found/NotFound'
import Button from '@components/Button'
import { defaultLocaleFromHost } from 'shared/country'
import { useGoogleRecaptcha } from 'core/hooks'
import featureToggles from 'shared/featureToggles'
import CookieModal from 'cookies/components/CookieModal'
import { getHandleReactIntlOnError } from './devUtils'

async function fetchTranslations(locale) {
    try {
        console.log('locale', locale)
        return await require(`i18n/locale/${locale}.json`)
    } catch (err) {
        console.error(err.message)
        return false
    }
}

function getActiveLocale(languages) {
    // check if WP has returned list of languages
    if (Object.keys(languages).length) {
        // and return locale from the one that is 'current: true'
        return Object.values(languages)
            .find(lang => lang.current)
            .locale.toLowerCase()
    } else {
        // otherwise return a predefined default locale
        return defaultLocaleFromHost()
    }
}

const App = ({ history, language, languages, wordpressLanguage, pending, path, isAuthVisible, configurationError }) => {
    useGoogleRecaptcha()
    const [translationStrings, setTranslationStrings] = React.useState({})
    const locale = getActiveLocale(languages)

    async function loadTranslations() {
        const translations = await fetchTranslations(locale)

        setTranslationStrings(translations)
    }

    React.useEffect(() => {
        loadTranslations()
    }, [locale])

    return (
        <IntlProvider locale={locale} messages={translationStrings} onError={getHandleReactIntlOnError()}>
            {configurationError ? (
                <NotFound status={<T>server.error.title</T>} message={<T>server.error.message</T>}>
                    <Button size="large" className="mt-5" onClick={() => location.reload()}>
                        <T>server.error.button-text</T>
                    </Button>
                </NotFound>
            ) : (
                <div id="pageScrollWrapper" style={{ height: '100vh', overflow: isAuthVisible ? 'hidden' : 'scroll' }}>
                    <Favicon />
                    <Helmet>
                        <title>Indigo</title>
                    </Helmet>

                    {!pending ? (
                        <>
                            {featureToggles.showCookiePolicy && <CookieModal />}
                            <Router history={history}>
                                {wordpressLanguage === language ? (
                                    <Routes />
                                ) : (
                                    <Redirect replace to={createPath(wordpressLanguage, stripLanguage(path))} />
                                )}
                            </Router>
                        </>
                    ) : (
                        <PleaseWait />
                    )}
                </div>
            )}
        </IntlProvider>
    )
}

export default connect(
    createStructuredSelector({
        languages: languagesSelector,
        language: pathLanguageSelector,
        wordpressLanguage: wordpressLanguageSelector,
        pending: configurationPendingSelector,
        configurationError: configurationErrorSelector,
        path: pathSelector,
        isAuthVisible: isAuthVisibleSelector,
    })
)(App)
